import axios from 'axios'
import API_CONFIG from '../../../../config/api'

/** Base da API (mesmo padrão de `_requests.ts` / login). */
const apiBaseUrl = () => process.env.REACT_APP_API_URL || API_CONFIG.BASE_URL

export type PublicApiKeyStatus = 'active' | 'expired' | 'revoked'

export type PublicApiScope =
  | 'tasks:read'
  | 'tasks:write'
  | 'projects:read'
  | 'projects:write'
  | 'crud:read'
  | 'crud:write'
  | 'time:read'
  | 'time:write'
  | 'time:read_all'
  | 'webhooks:read'
  | 'webhooks:write'

export type PublicApiKey = {
  id: number
  instance_id: number
  user_id: number
  name: string | null
  scopes: PublicApiScope[]
  is_legacy: boolean
  status: PublicApiKeyStatus
  created_at: string | null
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
}

export type PublicApiKeyIndexResponse = {
  data: PublicApiKey[]
}

export type PublicApiKeyCreateResponse = {
  api_token: string
  data: PublicApiKey
  message?: string
}

export type PublicApiKeyRevealResponse = {
  data: {api_token: string; id: number} | null
  message?: string
}

export type CreatePublicApiKeyPayload = {
  name: string
  scopes: PublicApiScope[]
  expires_at?: string | null
}

/**
 * Rótulo em português de cada escopo, agrupado por recurso (mesma ordem do catálogo do backend,
 * `App\Models\PublicApiKey::SCOPES`). `time:*` foi pro ar na Etapa 1a do programa de canais
 * (endpoints de timer e horas da Public API); `webhooks:*` foi pra ar na Etapa 5 (webhooks de
 * saída), nenhum dos dois é mais "em breve".
 */
export const PUBLIC_API_SCOPE_GROUPS: Array<{
  label: string
  scopes: Array<{value: PublicApiScope; label: string; comingSoon?: boolean}>
}> = [
  {
    label: 'Tarefas',
    scopes: [
      {value: 'tasks:read', label: 'Ler tarefas'},
      {value: 'tasks:write', label: 'Criar, editar e excluir tarefas'},
    ],
  },
  {
    label: 'Projetos',
    scopes: [
      {value: 'projects:read', label: 'Ler projetos'},
      {value: 'projects:write', label: 'Criar, editar e excluir projetos'},
    ],
  },
  {
    label: 'Clientes, times e usuários',
    scopes: [
      {value: 'crud:read', label: 'Ler clientes, times e usuários'},
      {
        value: 'crud:write',
        label: 'Criar, editar e excluir clientes, times e usuários (exige admin ou gestor)',
      },
    ],
  },
  {
    label: 'Tempo',
    scopes: [
      {value: 'time:read', label: 'Ler lançamentos de hora'},
      {value: 'time:write', label: 'Lançar e pausar hora'},
      {
        value: 'time:read_all',
        label: 'Ler o total de horas de todos os usuários (exige admin)',
      },
    ],
  },
  {
    label: 'Webhooks',
    scopes: [
      {value: 'webhooks:read', label: 'Ler assinaturas de webhook'},
      {
        value: 'webhooks:write',
        label: 'Criar, editar e remover assinaturas de webhook (exige admin ou gestor)',
      },
    ],
  },
]

class PublicApiKeysApi {
  /**
   * Usa o `axios` padrão (com `setupAxios` em `index.tsx`): mesmo Bearer JWT do restante do app.
   * Não usar `axios.create()` isolado — ele não recebe o interceptor que envia Authorization.
   */
  async list(): Promise<PublicApiKeyIndexResponse> {
    const {data} = await axios.get(`${apiBaseUrl()}/api/public-api-keys`)
    return data
  }

  async create(payload: CreatePublicApiKeyPayload): Promise<PublicApiKeyCreateResponse> {
    const {data} = await axios.post(`${apiBaseUrl()}/api/public-api-keys`, payload)
    return data
  }

  async revoke(id: number): Promise<void> {
    await axios.delete(`${apiBaseUrl()}/api/public-api-keys/${id}`)
  }

  /** Só funciona para o token legado (`is_legacy`), criado antes de vários tokens por usuário. */
  async reveal(): Promise<PublicApiKeyRevealResponse> {
    const {data} = await axios.get(`${apiBaseUrl()}/api/public-api-keys/reveal`)
    return data
  }
}

export const publicApiKeysApi = new PublicApiKeysApi()
